import { type FC, type FocusEvent as ReactFocusEvent, useEffect, useRef, useState } from 'react';

import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { isTextSelection, posToDOMRect } from '@tiptap/core';
import { AllSelection } from '@tiptap/pm/state';
import { type Editor, useEditorState } from '@tiptap/react';
import cn from 'classnames';

import { POPOVER_PANEL_CLASSES } from '../constants';
import { linkPromptKey } from '../nodes/LinkPrompt/LinkPrompt';
import { FormatButtons } from './FormatButtons';

interface SelectionFormatMenuProps {
  editor: Editor;
  translations?: Record<string, string>;
}

const isNodeInside = (node: EventTarget | null | undefined, root: Node | null | undefined): boolean =>
  node instanceof Node && Boolean(root?.contains(node));

const textRangeForMenu = (doc: Editor['state']['doc'], from: number, to: number) => {
  let textFrom: number | null = null;
  let textTo: number | null = null;

  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText || node.text === undefined) return;
    const nodeFrom = Math.max(from, pos);
    const nodeTo = Math.min(to, pos + node.nodeSize);
    if (nodeFrom >= nodeTo) return;
    if (textFrom === null) textFrom = nodeFrom;
    textTo = nodeTo;
  });

  if (textFrom === null || textTo === null) return { from, to };
  return { from: textFrom, to: textTo };
};

export const SelectionFormatMenu: FC<SelectionFormatMenuProps> = ({ editor, translations }) => {
  const selection = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const { doc, selection: current } = e.state;
      const { from, to } = current;

      return {
        from,
        to,
        qualifies:
          e.isEditable &&
          (isTextSelection(current) || current instanceof AllSelection) &&
          !current.empty &&
          doc.textBetween(from, to, ' ').trim().length > 0 &&
          !linkPromptKey.getState(e.state),
      };
    },
  });

  const [isEditorFocused, setIsEditorFocused] = useState(() => editor.view.hasFocus());
  const [isToolbarFocused, setIsToolbarFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isSelectingRef = useRef(false);
  const selectionSyncRafRef = useRef<number | null>(null);

  const { from, to, qualifies } = selection;
  const open = qualifies && !dismissed && (isEditorFocused || isToolbarFocused);

  const floating = useFloating({
    open,
    onOpenChange: (next, _event, reason) => {
      if (next) return;
      if (reason === 'escape-key') {
        setDismissed(true);
        return;
      }
      if (reason === 'outside-press') {
        setIsEditorFocused(false);
        setIsToolbarFocused(false);
      }
    },
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const interactions = useInteractions([
    useDismiss(floating.context, {
      ancestorScroll: false,
      outsidePressEvent: 'mousedown',
      outsidePress: (event) => !isNodeInside(event.target, editor.view.dom),
    }),
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset Escape-dismiss when the selection range changes
  useEffect(() => {
    setDismissed(false);
  }, [from, to]);

  useEffect(() => {
    const onFocus = () => {
      setIsEditorFocused(true);
      setDismissed(false);
    };
    const onBlur = (event?: Event) => {
      const next = event instanceof FocusEvent ? event.relatedTarget : null;
      if (isNodeInside(next, floating.refs.floating.current)) {
        setIsToolbarFocused(true);
        setIsEditorFocused(false);
        return;
      }
      if (isNodeInside(next, editor.view.dom)) return;
      // Drag-select often blurs on mouseup outside the editor with no relatedTarget.
      if (!next) return;
      setIsEditorFocused(false);
    };

    const handleTiptapBlur = ({ event }: { event: Event }) => onBlur(event);

    const cancelSelectionSync = () => {
      if (selectionSyncRafRef.current === null) return;
      cancelAnimationFrame(selectionSyncRafRef.current);
      selectionSyncRafRef.current = null;
    };

    const onMouseDown = () => {
      cancelSelectionSync();
      isSelectingRef.current = true;
    };

    const onMouseUp = () => {
      if (!isSelectingRef.current) return;
      isSelectingRef.current = false;
      cancelSelectionSync();
      selectionSyncRafRef.current = requestAnimationFrame(() => {
        selectionSyncRafRef.current = null;
        if (!editor.isDestroyed) setIsEditorFocused(true);
      });
    };

    const { dom } = editor.view;
    editor.on('focus', onFocus);
    editor.on('blur', handleTiptapBlur);
    dom.addEventListener('focus', onFocus);
    dom.addEventListener('blur', onBlur);
    dom.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      cancelSelectionSync();
      editor.off('focus', onFocus);
      editor.off('blur', handleTiptapBlur);
      dom.removeEventListener('focus', onFocus);
      dom.removeEventListener('blur', onBlur);
      dom.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [editor, floating.refs]);

  useEffect(() => {
    if (!open) return;

    floating.refs.setPositionReference({
      getBoundingClientRect: () => {
        if (editor.isDestroyed || to > editor.state.doc.content.size) {
          return { x: 0, y: 0, width: 0, height: 0, top: 0, bottom: 0, left: 0, right: 0 };
        }
        const textRange = textRangeForMenu(editor.state.doc, from, to);
        return posToDOMRect(editor.view, textRange.from, textRange.to);
      },
      contextElement: editor.view.dom,
    });
  }, [open, from, to, editor, floating.refs]);

  const handleToolbarFocus = () => {
    setIsToolbarFocused(true);
  };

  const handleToolbarBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    if (isNodeInside(event.relatedTarget, event.currentTarget)) return;

    setIsToolbarFocused(false);
    if (isNodeInside(event.relatedTarget, editor.view.dom)) {
      setIsEditorFocused(true);
    }
  };

  if (!open) return null;

  return (
    <FloatingPortal>
      <div
        ref={floating.refs.setFloating}
        style={{ ...floating.floatingStyles, visibility: floating.isPositioned ? 'visible' : 'hidden' }}
        className={cn(POPOVER_PANEL_CLASSES, 'z-[100] p-1')}
        {...interactions.getFloatingProps({
          onFocus: handleToolbarFocus,
          onBlur: handleToolbarBlur,
        })}
      >
        <FormatButtons editor={editor} translations={translations} />
      </div>
    </FloatingPortal>
  );
};
